
import SafeMotion from '@/components/motion/SafeMotion';
import MotionErrorBoundary from '@/components/motion/MotionErrorBoundary';
import { Button } from "@/components/ui/button";
import { Zap, Gift, Star } from "lucide-react";
import { Link } from "react-router-dom";
import SocialTrustBadges from "@/components/homepage/SocialTrustBadges";

const CallToActionSection = () => (
  <MotionErrorBoundary>
    <section className="py-16 sm:py-24 bg-gradient-luxury-cta relative overflow-hidden">
      {/* Luxury pattern overlay */}
      <SafeMotion 
        className="absolute inset-0 opacity-10" 
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%"]
        }} 
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "linear"
        }}
      >
        <div className="w-full h-full" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='hsl(45 100% 60%)' fill-opacity='0.3'%3E%3Cpath d='M40 40l20-20v20h-20zm0 0l-20 20h20v-20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </SafeMotion>
      
      {/* Floating luxury accents */}
      <div className="absolute top-10 left-10 w-20 h-20 bg-luxury-gold/10 rounded-full blur-xl animate-pulse" />
      <div className="absolute bottom-10 right-10 w-32 h-32 bg-luxury-rose-gold/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '2s' }} />
      <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-luxury-champagne/20 rounded-full blur-lg animate-pulse" style={{ animationDelay: '4s' }} />
      
      <div className="w-full px-4 md:px-8 lg:px-12 text-center relative">
        <SafeMotion 
          initial={{
            opacity: 0,
            y: 40,
            scale: 0.95
          }} 
          animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} 
          transition={{
            duration: 1,
            type: "spring",
            stiffness: 100
          }} 
          className="max-w-5xl mx-auto bg-gradient-luxury-subtle backdrop-blur-xl border border-luxury-gold/30 rounded-3xl p-10 sm:p-16 shadow-luxury-feature relative overflow-hidden"
        >
          {/* Luxury border accents */}
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-[2px] bg-gradient-to-r from-transparent via-luxury-gold to-transparent" />
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-32 h-[2px] bg-gradient-to-r from-transparent via-luxury-gold to-transparent" />
          <SafeMotion 
            whileHover={{
              scale: 1.15,
              rotate: [0, -12, 12, 0],
              filter: ["hue-rotate(0deg)", "hue-rotate(15deg)", "hue-rotate(-15deg)", "hue-rotate(0deg)"]
            }} 
            transition={{
              duration: 0.6,
              type: "spring",
              stiffness: 200
            }}
            className="relative mb-8 sm:mb-10"
          >
            <div className="absolute inset-0 bg-luxury-gold/20 rounded-full blur-xl animate-pulse" />
            <Gift className="h-16 w-16 sm:h-20 sm:w-20 text-luxury-gold mx-auto relative z-10 filter drop-shadow-lg" />
          </SafeMotion>
          
          <h2 className="text-3xl sm:text-5xl font-bold mb-4 sm:mb-8 bg-gradient-to-r from-luxury-gold via-luxury-rose-gold to-luxury-gold bg-clip-text text-transparent leading-tight">
            Ready to Experience Luxury?
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground mb-10 sm:mb-14 max-w-4xl mx-auto leading-relaxed font-medium">
            Join thousands of discerning customers and discover what makes VRAM the pinnacle of luxury e-commerce
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 justify-center mb-10 sm:mb-16">
            <SafeMotion 
              whileHover={{
                scale: 1.08,
                y: -4,
                boxShadow: "0 20px 40px -8px hsl(var(--luxury-gold) / 0.4)"
              }} 
              whileTap={{
                scale: 0.98
              }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <Button asChild size="lg" className="relative min-h-touch mobile-optimized px-10 py-5 text-lg font-bold rounded-full bg-gradient-to-r from-luxury-gold via-luxury-rose-gold to-luxury-gold text-primary-foreground shadow-luxury-glow hover:shadow-luxury-hover transition-all duration-500 border border-luxury-gold/50 overflow-hidden group">
                <Link to="/products" className="relative z-10">
                  <SafeMotion
                    whileHover={{ scale: 1.2, rotate: 15 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Zap className="mr-3 h-6 w-6" />
                  </SafeMotion>
                  <span>Explore Luxury Collection</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-luxury-gold-light via-luxury-champagne to-luxury-gold-light opacity-0 group-hover:opacity-20 transition-opacity duration-500" />
                </Link>
              </Button>
            </SafeMotion>
            
            <SafeMotion 
              whileHover={{
                scale: 1.08,
                y: -4,
                borderColor: "hsl(var(--luxury-gold))"
              }} 
              whileTap={{
                scale: 0.98
              }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <Button asChild variant="outline" size="lg" className="min-h-touch mobile-optimized px-10 py-5 text-lg font-bold rounded-full border-2 border-luxury-gold/40 hover:border-luxury-gold text-luxury-gold hover:text-luxury-gold-dark backdrop-blur-sm bg-luxury-champagne/10 hover:bg-luxury-champagne/20 shadow-luxury-feature hover:shadow-luxury-hover transition-all duration-500 relative overflow-hidden group">
                <Link to="/auth" className="relative z-10">
                  <span>Join the Elite</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-luxury-gold/10 to-luxury-champagne/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </Link>
              </Button>
            </SafeMotion>
          </div>
          
          <SocialTrustBadges />
        </SafeMotion>
      </div>
    </section>
  </MotionErrorBoundary>
);

export default CallToActionSection;
